import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Per-history-entry scroll restoration for the app shell's INNER scroll
 * container.
 *
 * Why this has to exist at all: the browser's own scroll restoration (and
 * react-router's `<ScrollRestoration/>`) only ever touches WINDOW scroll. This
 * shell never scrolls the window — `AppLayout` pins the grid to `100dvh` and
 * puts `overflow-y-auto` on `<main>` — so the native mechanism restores
 * literally nothing. Going Back from a tender dumped the reader at the top of
 * a long catalog list, which on mobile (99% of traffic) is the difference
 * between browsing and giving up.
 *
 * Companion to `useScrollResetOnNavigate` (see `./scroll-reset`), which zeroes
 * the same container on FORWARD navigations. The two never fight: this hook
 * only acts on `POP`, that one only acts on non-`POP`.
 *
 * Generic to the shell: it is handed the scroll container, keys everything on
 * the history entry, and knows nothing about the catalog or any other route.
 */

interface StoredOffset {
  /** `pathname + search` the offset was captured at — see `getScrollOffset`. */
  url: string;
  top: number;
}

const STORAGE_KEY = 'tenders.scroll-offsets';

/** Cap the map so a long browsing session can't grow sessionStorage unbounded. */
const MAX_ENTRIES = 30;

/**
 * Frame budget for a restore attempt (~4s at 60fps). The list is async, so on
 * `POP` the container is often empty at first paint and `scrollTop = N` would
 * silently clamp to 0. We retry per animation frame until the content is tall
 * enough — but BOUNDED, so a route that never loads can't leave a loop ticking
 * forever.
 *
 * 4s rather than something tighter because it is measured, not guessed: on the
 * real catalog against the production API the container only grew to full
 * height ~1.8s after the Back, and a 1.5s budget gave up first and left the
 * reader at a partially-restored offset. Waiting costs a couple of clean
 * property reads per frame (writing `scrollTop` doesn't dirty layout), and a
 * late restore can't hijack anyone: the moment the reader touches the
 * wheel/screen/keys we bail out for good.
 */
const MAX_RESTORE_FRAMES = 240;

let cache: Map<string, StoredOffset> | null = null;

/**
 * The offsets, keyed by `location.key` (react-router's per-history-entry id).
 *
 * Backed by `sessionStorage`, not just a module-level Map: mobile Safari
 * discards backgrounded tabs and re-loads the page when the user returns,
 * which wipes in-memory state while leaving the history entries (and their
 * keys, which react-router persists in `history.state`) intact. The Map is the
 * hot path; storage is the mirror. Both are per-tab, which is the right scope
 * — a second tab has its own history.
 */
function store(): Map<string, StoredOffset> {
  if (cache) return cache;
  const map = new Map<string, StoredOffset>();
  cache = map;
  if (typeof sessionStorage === 'undefined') return map;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return map;
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return map;
    for (const entry of parsed) {
      if (!Array.isArray(entry) || entry.length !== 2) continue;
      const [key, value] = entry as [unknown, unknown];
      const offset = value as Partial<StoredOffset> | null;
      if (typeof key !== 'string' || !offset) continue;
      if (typeof offset.url !== 'string' || typeof offset.top !== 'number') continue;
      map.set(key, { url: offset.url, top: offset.top });
    }
  } catch {
    // Private-mode storage, a quota error, or a corrupt payload: start empty.
    // Losing scroll positions is a papercut, throwing here would blank the app.
  }
  return map;
}

function persist(map: Map<string, StoredOffset>): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...map]));
  } catch {
    // Quota/private mode — the in-memory Map still serves the common case.
  }
}

/** Record where a history entry was scrolled to. Exported for tests. */
export function saveScrollOffset(key: string, url: string, top: number): void {
  const map = store();
  // Delete-then-set so the Map's insertion order stays LRU and the trim below
  // evicts the least recently *visited* entry, not the oldest-created one.
  map.delete(key);
  map.set(key, { url, top: Math.max(0, Math.round(top)) });
  let overflow = map.size - MAX_ENTRIES;
  for (const oldest of map.keys()) {
    if (overflow-- <= 0) break;
    map.delete(oldest);
  }
  persist(map);
}

/**
 * The offset stored for a history entry, or `null`.
 *
 * The URL is checked as well as the key because `location.key` is NOT globally
 * unique: react-router labels the first entry of every full page load
 * `'default'`. Without this guard, landing on a different page via a hard
 * navigation in the same tab could inherit the previous landing page's offset.
 */
export function getScrollOffset(key: string, url: string): number | null {
  const entry = store().get(key);
  if (!entry || entry.url !== url) return null;
  return entry.top;
}

/** Drop every stored offset (test helper; also used if storage goes bad). */
export function clearScrollOffsets(): void {
  cache = new Map();
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* nothing to do */
  }
}

/**
 * Drive `el` back to `target`, coping with content that arrives after mount.
 *
 * Each frame we clamp to the tallest position the content currently allows, so
 * the container converges on the target as rows stream in instead of jumping
 * once at the end. We stop the moment the full target is reachable, when the
 * frame budget runs out, or as soon as the user touches the wheel/screen/keys
 * — yanking someone who has already started scrolling is worse than not
 * restoring at all.
 *
 * Writes happen inside `requestAnimationFrame`, i.e. before the frame paints,
 * so there is no visible scroll flash.
 *
 * `onScrollTop` is notified of every offset we write, so the caller's
 * last-known-offset tracking doesn't have to wait for the (asynchronous)
 * `scroll` event our own writes emit.
 *
 * Returns a cancel function; the caller MUST call it on navigation/unmount.
 */
function restoreScrollTop(
  el: HTMLElement,
  target: number,
  onScrollTop: (top: number) => void,
): () => void {
  let frames = 0;
  let raf = 0;
  let stopped = false;

  const attempt = (): boolean => {
    const max = Math.max(0, el.scrollHeight - el.clientHeight);
    const next = Math.min(target, max);
    if (el.scrollTop !== next) {
      el.scrollTop = next;
      onScrollTop(el.scrollTop);
    }
    return max >= target;
  };

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    if (raf) cancelAnimationFrame(raf);
    el.removeEventListener('wheel', stop);
    el.removeEventListener('touchstart', stop);
    el.removeEventListener('keydown', stop);
  };

  const tick = (): void => {
    if (stopped) return;
    if (attempt() || ++frames >= MAX_RESTORE_FRAMES) {
      stop();
      return;
    }
    raf = requestAnimationFrame(tick);
  };

  el.addEventListener('wheel', stop, { passive: true });
  el.addEventListener('touchstart', stop, { passive: true });
  el.addEventListener('keydown', stop);

  // Try synchronously first: when the data is already in the react-query cache
  // the content is there at commit time and this lands before the first paint.
  if (attempt()) stop();
  else raf = requestAnimationFrame(tick);

  return stop;
}

// The shell is server-rendered/prerendered, where `useLayoutEffect` warns and
// never runs anyway. On the client we need the layout phase: restoring after
// paint would show one frame at the wrong offset.
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * Remembers `ref`'s scroll offset per history entry and puts it back on
 * Back/Forward.
 *
 * Restores on `POP` only — a fresh navigation must start at the top (that is
 * `useScrollResetOnNavigate`'s job), and a push to a URL the user has visited
 * before is a NEW entry with a new key, so it deliberately gets no offset.
 *
 * Does NOT recover the position when the history entry itself is gone: a link
 * opened in a new tab, a different browser session, or a Back that leaves the
 * SPA entirely (external referrer) all start clean. A hard reload of the
 * current entry DOES recover, because react-router keeps the entry key in
 * `history.state` and the offsets are mirrored into `sessionStorage`.
 */
export function useScrollRestoration(ref: RefObject<HTMLElement | null>): void {
  const { key, pathname, search } = useLocation();
  const navigationType = useNavigationType();
  const url = `${pathname}${search}`;
  const latestTop = useRef(0);

  // Track the offset continuously instead of reading `scrollTop` when we come
  // to save it.
  //
  // Reading it at save time looks correct and is not: React tears the OUTGOING
  // route's DOM down (mutation phase, children first) BEFORE it runs this
  // component's cleanup, and the browser clamps `scrollTop` to the suddenly
  // much shorter content the instant that happens. By the time any cleanup
  // could read the element, the number is already 0 — verified in a real
  // browser, where a catalog scrolled to 1200px persisted `{"top":0}`. The
  // last value observed while the content still existed is the true one.
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Seed from the DOM: a new entry starts wherever it starts, and an entry we
    // are about to restore is corrected by `restoreScrollTop`'s callback below.
    latestTop.current = el.scrollTop;
    const onScroll = (): void => void (latestTop.current = el.scrollTop);
    const save = (): void => saveScrollOffset(key, url, latestTop.current);
    el.addEventListener('scroll', onScroll, { passive: true });
    // A tab discard / bfcache eviction never runs React cleanups.
    window.addEventListener('pagehide', save);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('pagehide', save);
      save();
    };
  }, [key, url, ref]);

  useIsomorphicLayoutEffect(() => {
    if (navigationType !== 'POP') return;
    const el = ref.current;
    if (!el) return;
    const target = getScrollOffset(key, url);
    if (!target) return;
    return restoreScrollTop(el, target, (top) => void (latestTop.current = top));
  }, [key, url, navigationType, ref]);
}
